import React from 'react';
import { GitBranch, ArrowRight } from 'lucide-react';
import { fmtQty, pct } from './phasedUtils';

const fmt = [(v) => fmtQty(Math.round(v * 100) / 100), (v) => v.toFixed(1), (v) => v.toFixed(2)];

const Row = ({ label, values, strong, testid }) => (
  <tr className={strong ? 'font-semibold text-foreground' : 'text-muted-foreground'} data-testid={testid}>
    <td className="py-0.5 pr-2 font-sans whitespace-nowrap">{label}</td>
    {values.map((v, i) => <td key={i} className="py-0.5 pl-2 text-right">{fmt[i](v)}</td>)}
  </tr>
);

const TotalCell = ({ value, target, i }) => {
  const p = pct(value, target);
  const off = Math.abs(p) > 0.1;
  return (
    <td className={`py-1 pl-2 text-right whitespace-nowrap ${off ? 'text-amber-700' : 'text-emerald-700'}`}>
      {fmt[i](value)}<span className="opacity-60"> / {fmt[i](target)}</span>
      {off && <div className="text-[10px]">{p > 0 ? '+' : ''}{p.toFixed(1)}%</div>}
    </td>
  );
};

const CarryHint = ({ tally, next }) => {
  const { carry } = tally;
  if (Math.abs(carry) < 0.001) return null;
  const qty = fmtQty(Math.abs(carry));
  const text = next
    ? `On confirm, Part ${next.part_number} (#${next.dispatch_number}) ${carry > 0 ? `gets +${qty}` : `drops by ${qty}`} units to keep the PO whole`
    : `No open part left: ${qty} units will show as ${carry > 0 ? 'short' : 'over'} on the PO`;
  return (
    <div className={`flex items-start gap-1 border-t px-2 py-1 ${next ? 'text-primary' : 'text-amber-700'}`} data-testid="po-tally-carry">
      <ArrowRight className="mt-0.5 h-3 w-3 flex-shrink-0" /><span>{text}</span>
    </div>
  );
};

export const PoTallyPanel = ({ tally, next, label, index }) => (
  <div className="w-max min-w-[400px] rounded-md border border-primary/25 bg-primary/[0.03] text-[11px]" data-testid={`po-tally-${index}`}>
    <div className="flex items-center gap-1.5 border-b px-2 py-1 font-semibold text-primary">
      <GitBranch className="h-3 w-3" /> Whole PO{label ? ` · ${label}` : ''}
    </div>
    <table className="w-full px-2 font-mono">
      <thead>
        <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
          <th /><th className="pl-2 text-right font-medium">Units</th><th className="pl-2 text-right font-medium">Cases</th><th className="pl-2 pr-2 text-right font-medium">Kg</th>
        </tr>
      </thead>
      <tbody className="[&_td:first-child]:pl-2 [&_td:last-child]:pr-2">
        <Row label="Sent in earlier parts" values={tally.sent} testid={`po-tally-sent-${index}`} />
        <Row label="This delivery" values={tally.now} strong testid={`po-tally-now-${index}`} />
        <Row label="Open in other parts" values={tally.others} testid={`po-tally-open-${index}`} />
        {tally.short[0] > 0 && <Row label="Closed short" values={tally.short} testid={`po-tally-short-${index}`} />}
        <tr className="border-t font-semibold" data-testid={`po-tally-total-${index}`}>
          <td className="py-1 pl-2 pr-2 font-sans whitespace-nowrap">PO total</td>
          {tally.total.map((v, i) => <TotalCell key={i} value={v} target={tally.ordered[i]} i={i} />)}
        </tr>
      </tbody>
    </table>
    <CarryHint tally={tally} next={next} />
  </div>
);
